/**
 * Interviu ghidat cu agentul AI (ușa 2, spec §4). Conversație structurată: o întrebare
 * pe rând, cu chip-uri de răspuns rapid. La final: șablon + personaj + voce → creează clipul.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProductCategory, ProductTier, VideoFormat } from '@reclame-ai/shared';
import { VIDEO_FORMATS } from '@reclame-ai/shared';
import { answerInterview, createInterviewClip, startInterview } from '../api/endpoints';
import { useCharacters, useMutation, useTemplates, useVoices } from '../api/hooks';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import type { InterviewSession } from '../api/types';
import { Logo } from '../components/Logo';
import { Spinner } from '../components/ui';
import { VoicePlayButton } from '../components/VoicePlayButton';
import { categoryLabel, tierLabel } from '../lib/format';
import { resolveMediaUrl } from '../lib/media';

export function InterviewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [category, setCategory] = useState<ProductCategory>('organic');
  const [tier, setTier] = useState<ProductTier>('premium');
  const [format, setFormat] = useState<VideoFormat>('9:16');
  const [session, setSession] = useState<InterviewSession | null>(null);
  const [answer, setAnswer] = useState('');
  const [transcript, setTranscript] = useState<{ who: 'ai' | 'me'; text: string }[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [characterId, setCharacterId] = useState<string | null>(null);
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const templates = useTemplates(category);
  const voices = useVoices();

  const template = useMemo(
    () => templates.data?.items.find((t) => t.id === selectedTemplate) ?? null,
    [templates.data, selectedTemplate],
  );
  const templateCharacterIds = template?.characterIds ?? [];
  const charactersQuery = useCharacters(
    templateCharacterIds.length > 0 ? templateCharacterIds : undefined,
  );
  const characters = charactersQuery.data?.items ?? [];

  useEffect(() => {
    document.title = 'Interviu ghidat — Reclame Video';
  }, []);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, selectedTemplate, characterId]);

  // La alegerea șablonului: preselectează personaj + voce default.
  useEffect(() => {
    if (!template) return;
    if (characters.length > 0 && !characterId) setCharacterId(characters[0]!.id);
    if (!voiceId) {
      const list = voices.data?.items ?? [];
      const rec =
        list.find((v) => v.recommended) ??
        (template.defaultVoiceId ? list.find((v) => v.id === template.defaultVoiceId) : null) ??
        list[0];
      if (rec) setVoiceId(rec.id);
    }
  }, [template, characters, voices.data, characterId, voiceId]);

  // Reset personaj când se schimbă șablonul.
  useEffect(() => {
    setCharacterId(null);
  }, [selectedTemplate]);

  const startMut = useMutation({
    mutationFn: () => startInterview({ category, tier, format }),
    onSuccess: (s) => {
      setSession(s);
      if (s.question) setTranscript([{ who: 'ai', text: s.question.text }]);
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });

  const answerMut = useMutation({
    mutationFn: (text: string) => answerInterview(session!.id, text),
    onSuccess: ({ session: s }) => {
      setSession(s);
      setTranscript((t) => (s.question ? [...t, { who: 'ai', text: s.question!.text }] : t));
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      createInterviewClip(session!.id, {
        templateId: selectedTemplate ?? undefined,
        characterId: characterId ?? undefined,
        voiceId: voiceId ?? undefined,
      }),
    onSuccess: (res) => navigate(`/clipuri/${res.clip.id}`, { replace: true }),
    onError: (err) => toast.push({ kind: 'err', title: 'Nu am putut crea clipul', body: humanError(err) }),
  });

  function submit(text: string) {
    if (!text.trim()) return;
    setTranscript((t) => [...t, { who: 'me', text }]);
    setAnswer('');
    answerMut.mutate(text);
  }

  const done = session?.status === 'finalizat';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ position: 'sticky', top: 0, background: 'var(--hdr)', backdropFilter: 'blur(16px)', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px' }}>
        <button onClick={() => navigate('/creeaza')} style={{ fontSize: 13, fontWeight: 800, color: 'var(--muted)' }}>← Înapoi la cele 3 opțiuni</button>
        <span style={{ marginLeft: 'auto' }}><Logo /></span>
      </header>

      <div style={{ flex: 1, width: 'min(640px, 100%)', margin: '0 auto', padding: '24px 18px 40px', boxSizing: 'border-box' }}>
        {!session ? (
          <div className="card rv-in" style={{ padding: 22 }}>
            <h1 className="bricolage" style={{ fontSize: 24, fontWeight: 800 }}>Hai să facem clipul împreună</h1>
            <p style={{ margin: '8px 0 18px', fontSize: 13.5, color: 'var(--muted)' }}>
              Îți pun câteva întrebări scurte — o singură întrebare o dată, răspunzi cu un click. La final alegi șablonul, personajul și vocea.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <ChoiceRow label="Categorie">
                {(['organic', 'reclame'] as ProductCategory[]).map((c) => (
                  <button key={c} className="chip" data-on={category === c} onClick={() => setCategory(c)}>{categoryLabel(c)}</button>
                ))}
              </ChoiceRow>
              <ChoiceRow label="Tip">
                {(['premium', 'ultra'] as ProductTier[]).map((t) => (
                  <button key={t} className="chip" data-on={tier === t} onClick={() => setTier(t)}>{tierLabel(t)}</button>
                ))}
              </ChoiceRow>
              <ChoiceRow label="Format">
                {VIDEO_FORMATS.map((f) => (
                  <button key={f} className="chip" data-on={format === f} onClick={() => setFormat(f)}>{f}</button>
                ))}
              </ChoiceRow>
            </div>
            <button className="btn btn-primary" data-testid="start-interview" onClick={() => startMut.mutate()} disabled={startMut.isPending} style={{ marginTop: 18 }}>
              {startMut.isPending ? <Spinner /> : 'Pornește interviul →'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {transcript.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.who === 'me' ? 'flex-end' : 'flex-start' }}>
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '11px 14px',
                    borderRadius: 14,
                    fontSize: 14,
                    background: m.who === 'me' ? 'var(--accent)' : 'var(--surface)',
                    color: m.who === 'me' ? 'var(--accent-ink)' : 'var(--ink)',
                    border: m.who === 'me' ? 'none' : '1px solid var(--line)',
                    fontWeight: m.who === 'me' ? 700 : 600,
                  }}
                >
                  {m.text}
                </div>
              </div>
            ))}

            {!done && session.question && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} data-testid="interview-chips">
                  {session.question.chips.map((chip) => (
                    <button key={chip} className="chip" onClick={() => submit(chip)} disabled={answerMut.isPending}>
                      {chip}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="input"
                    data-testid="interview-answer"
                    placeholder="…sau scrie răspunsul tău"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submit(answer)}
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => submit(answer)} disabled={answerMut.isPending || !answer.trim()}>
                    {answerMut.isPending ? <Spinner /> : 'Trimite'}
                  </button>
                </div>
              </div>
            )}

            {done && (
              <div className="card rv-in" data-testid="interview-proposal" style={{ padding: 20, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <span className="pill" style={{ alignSelf: 'flex-start', background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>Propunerea agentului</span>
                <h2 className="bricolage" style={{ fontSize: 20, fontWeight: 800 }}>Gata! Alege prezentarea clipului</h2>
                {session.brief && (
                  <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6 }}>
                    <p style={{ margin: 0 }}><strong style={{ color: 'var(--ink)' }}>Rezumat:</strong> {session.brief.summary}</p>
                  </div>
                )}

                <div>
                  <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 800 }}>1. Șablon</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(templates.data?.items ?? []).slice(0, 6).map((t) => (
                      <button
                        key={t.id}
                        className="chip"
                        data-on={selectedTemplate === t.id}
                        onClick={() => setSelectedTemplate(t.id)}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedTemplate && (
                  <>
                    <div>
                      <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 800 }}>2. Personaj</p>
                      {charactersQuery.isLoading ? (
                        <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>Se încarcă…</p>
                      ) : characters.length === 0 ? (
                        <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                          Niciun personaj în catalog — poți crea clipul fără (doar voce + cadre generate).
                        </p>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} data-testid="interview-characters">
                          {characters.map((ch) => (
                            <button
                              key={ch.id}
                              type="button"
                              className="chip"
                              data-on={characterId === ch.id}
                              onClick={() => setCharacterId(ch.id)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                              {ch.cover?.url && (
                                <img
                                  src={resolveMediaUrl(ch.cover.url) ?? ch.cover.url}
                                  alt=""
                                  style={{ width: 20, height: 20, borderRadius: 999, objectFit: 'cover' }}
                                />
                              )}
                              {ch.name}
                            </button>
                          ))}
                          <button
                            type="button"
                            className="chip"
                            data-on={characterId === null}
                            onClick={() => setCharacterId(null)}
                          >
                            Fără personaj
                          </button>
                        </div>
                      )}
                    </div>

                    <div>
                      <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 800 }}>3. Voce</p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }} data-testid="interview-voices">
                        {(voices.data?.items ?? []).slice(0, 8).map((v) => (
                          <span key={v.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <button
                              type="button"
                              className="chip"
                              data-on={voiceId === v.id}
                              onClick={() => setVoiceId(v.id)}
                            >
                              {v.name}
                              {v.recommended ? ' · recomandat' : ''}
                            </button>
                            <VoicePlayButton voiceId={v.id} sampleUrl={v.sampleUrl} label={v.name} />
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <button
                  className="btn btn-primary"
                  data-testid="interview-create-clip"
                  onClick={() => createMut.mutate()}
                  disabled={createMut.isPending || !selectedTemplate}
                >
                  {createMut.isPending ? <Spinner /> : 'Creează clipul și treci la scenariu →'}
                </button>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>
                  Nimic nu se consumă din sold până aprobi imaginile.
                </p>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}
      </div>
    </div>
  );
}

function ChoiceRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--muted)', minWidth: 70 }}>{label}:</span>
      {children}
    </div>
  );
}
